import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Users, UserX, UserCheck, KeyRound, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface UserWithInvitation {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  role: string;
  isActive: boolean;
  createdAt: string;
  invitation: {
    companyName: string;
  } | null;
}

interface UsersResponse {
  data: UserWithInvitation[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

async function fetchUsers(
  page: number,
  search: string,
  role: string,
  status: string
): Promise<UsersResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "10" });
  if (search) params.set("search", search);
  if (role !== "all") params.set("role", role);
  if (status !== "all") params.set("status", status);
  const response = await fetch(`/api/admin/users?${params}`, { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch users");
  return response.json();
}

async function updateUserPassword(userId: string, newPassword: string) {
  const response = await fetch(`/api/admin/users/${userId}/password`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ newPassword }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to update password");
  }
  return response.json();
}

async function toggleUserActive(userId: string) {
  const response = await fetch(`/api/admin/users/${userId}/toggle-active`, {
    method: "PATCH",
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to toggle user status");
  }
  return response.json();
}

export default function AdminUsers() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [editPasswordUser, setEditPasswordUser] = useState<UserWithInvitation | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserFirstName, setNewUserFirstName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserConfirmPassword, setNewUserConfirmPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<"platform_assistant" | "sales_person">("platform_assistant");
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/users", currentPage, debouncedSearch, roleFilter, statusFilter],
    queryFn: () => fetchUsers(currentPage, debouncedSearch, roleFilter, statusFilter),
    enabled: user?.role === "admin",
  });

  const toggleActiveMutation = useMutation({
    mutationFn: toggleUserActive,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: data.isActive ? "Account enabled" : "Account disabled",
        description: data.isActive ? "User account has been enabled." : "User account has been disabled.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      updateUserPassword(userId, newPassword),
    onSuccess: () => {
      setEditPasswordUser(null);
      setNewPassword("");
      setConfirmPassword("");
      toast({ title: "Password updated", description: "The user's password has been changed successfully." });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update password", description: error.message, variant: "destructive" });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (data: { email: string; firstName: string; lastName: string; password: string; role: string }) => {
      const response = await fetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setAddUserOpen(false);
      setNewUserEmail("");
      setNewUserFirstName("");
      setNewUserLastName("");
      setNewUserPassword("");
      setNewUserConfirmPassword("");
      setNewUserRole("platform_assistant");
      toast({ title: "Account created", description: "The account has been created successfully." });
    },
    onError: (error: Error) => {
      toast({ title: "Email already in use", description: error.message, variant: "destructive" });
    },
  });

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">{"Access Denied"}</h3>
            <p className="text-sm text-slate-500 mt-1">{"You must be a platform administrator to view this page."}</p>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  const columns: ColumnDef<UserWithInvitation>[] = [
    {
      id: "name",
      header: "Name",
      cell: (u) => (
        <span className="font-semibold text-slate-900">
          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : "-"}
        </span>
      ),
    },
    {
      id: "email",
      header: "Email",
      cell: (u) => <span className="text-slate-700">{u.email}</span>,
    },
    {
      id: "company",
      header: "Company",
      cell: (u) => <span className="text-slate-700">{u.companyName || u.invitation?.companyName || "-"}</span>,
    },
    {
      id: "role",
      header: "Role",
      cell: (u) => (
        <span className="px-2.5 py-1 bg-primary/10 rounded-full text-primary text-xs font-bold capitalize">
          {u.role.replace("_", " ")}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (u) =>
        u.isActive ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {"Active"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-600">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            {"Inactive"}
          </span>
        ),
    },
    {
      id: "joined",
      header: "Joined",
      cell: (u) => <span className="text-sm text-slate-500">{u.createdAt ? formatDate(u.createdAt) : "-"}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      cell: (u) =>
        u.role !== "admin" ? (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => {
                setEditPasswordUser(u);
                setNewPassword("");
                setConfirmPassword("");
              }}
              title={"Reset password"}
            >
              <KeyRound className="h-4 w-4 text-primary" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => toggleActiveMutation.mutate(u.id)}
              disabled={toggleActiveMutation.isPending}
              title={u.isActive ? "Disable account" : "Enable account"}
            >
              {u.isActive ? (
                <UserX className="h-4 w-4 text-amber-500" />
              ) : (
                <UserCheck className="h-4 w-4 text-emerald-600" />
              )}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">
            {"User Management"}
          </h1>
          <p className="text-slate-500 mt-2">
            {"View and manage all platform users"}
          </p>
        </div>
        <Button className="rounded-2xl gap-2" onClick={() => setAddUserOpen(true)}>
          <UserPlus className="h-4 w-4" />
          {"Add Staff Account"}
        </Button>
      </div>

      <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {`Users (${data?.total ?? 0})`}
            </h3>
          </div>
        </div>
        <div className="p-6">
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            getRowId={(u) => u.id}
            isLoading={isLoading}
            emptyState={{ icon: Users, title: "No users found" }}
            search={{
              value: search,
              onChange: setSearch,
              placeholder: "Search by name, email, or company...",
            }}
            filters={
              <>
                <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[160px] rounded-2xl bg-slate-50">
                    <SelectValue placeholder={"Filter by role"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{"All Roles"}</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="company_admin">{"Company Admin"}</SelectItem>
                    <SelectItem value="member">{"Member"}</SelectItem>
                    <SelectItem value="contributor">{"Contributor"}</SelectItem>
                    <SelectItem value="sales_person">{"Sales Person"}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[160px] rounded-2xl bg-slate-50">
                    <SelectValue placeholder={"Filter by status"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{"All Status"}</SelectItem>
                    <SelectItem value="active">{"Active"}</SelectItem>
                    <SelectItem value="inactive">{"Inactive"}</SelectItem>
                  </SelectContent>
                </Select>
              </>
            }
            pagination={
              data
                ? {
                    page: currentPage,
                    pageSize: data.pageSize,
                    total: data.total,
                    totalPages: data.totalPages,
                    onPageChange: setCurrentPage,
                  }
                : undefined
            }
          />
        </div>
      </div>

      {/* Add staff account dialog */}
      <Dialog
        open={addUserOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddUserOpen(false);
            setNewUserEmail("");
            setNewUserFirstName("");
            setNewUserLastName("");
            setNewUserPassword("");
            setNewUserConfirmPassword("");
            setNewUserRole("platform_assistant");
          }
        }}
      >
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{"Add Staff Account"}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {"Create a new platform assistant or sales person account."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-user-email" className="text-slate-700">{"Email"}</Label>
              <Input
                id="new-user-email"
                type="email"
                placeholder="assistant@example.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                className="bg-slate-50 rounded-2xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-user-role" className="text-slate-700">{"Role"}</Label>
              <Select value={newUserRole} onValueChange={(v) => setNewUserRole(v as "platform_assistant" | "sales_person")}>
                <SelectTrigger id="new-user-role" className="bg-slate-50 rounded-2xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="platform_assistant">{"Platform Assistant"}</SelectItem>
                  <SelectItem value="sales_person">{"Sales Person"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="new-user-firstname" className="text-slate-700">{"First name"}</Label>
                <Input
                  id="new-user-firstname"
                  type="text"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                  className="bg-slate-50 rounded-2xl"
                />
              </div>
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="new-user-lastname" className="text-slate-700">{"Last name"}</Label>
                <Input
                  id="new-user-lastname"
                  type="text"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                  className="bg-slate-50 rounded-2xl"
                />
              </div>
            </div>
            <div className="h-px bg-slate-200" />
            <div className="space-y-1.5">
              <Label htmlFor="new-user-password" className="text-slate-700">{"New Password"}</Label>
              <Input
                id="new-user-password"
                type="password"
                placeholder={"Min. 8 characters"}
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                className="bg-slate-50 rounded-2xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-user-confirm-password" className="text-slate-700">{"Confirm Password"}</Label>
              <Input
                id="new-user-confirm-password"
                type="password"
                placeholder={"Re-enter new password"}
                value={newUserConfirmPassword}
                onChange={(e) => setNewUserConfirmPassword(e.target.value)}
                className="bg-slate-50 rounded-2xl"
              />
              {newUserConfirmPassword && newUserPassword !== newUserConfirmPassword && (
                <p className="text-xs text-red-500">{"Passwords do not match."}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-2xl"
              onClick={() => setAddUserOpen(false)}
            >
              {"Cancel"}
            </Button>
            <Button
              className="rounded-2xl"
              onClick={() =>
                createUserMutation.mutate({
                  email: newUserEmail,
                  firstName: newUserFirstName,
                  lastName: newUserLastName,
                  password: newUserPassword,
                  role: newUserRole,
                })
              }
              disabled={
                !newUserEmail ||
                !newUserPassword ||
                newUserPassword.length < 8 ||
                newUserPassword !== newUserConfirmPassword ||
                createUserMutation.isPending
              }
            >
              {createUserMutation.isPending ? "Updating..." : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog
        open={!!editPasswordUser}
        onOpenChange={(open) => {
          if (!open) {
            setEditPasswordUser(null);
            setNewPassword("");
            setConfirmPassword("");
          }
        }}
      >
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{"Reset User Password"}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {"Set a new password for this user account."}
            </DialogDescription>
          </DialogHeader>

          {editPasswordUser && (
            <>
              <div className="space-y-3 py-1">
                <div className="p-4 bg-slate-50 rounded-2xl space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{"Name"}</span>
                    <span className="font-semibold text-slate-900">
                      {editPasswordUser.firstName && editPasswordUser.lastName
                        ? `${editPasswordUser.firstName} ${editPasswordUser.lastName}`
                        : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{"Email"}</span>
                    <span className="font-semibold text-slate-900">{editPasswordUser.email}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{"Company"}</span>
                    <span className="font-semibold text-slate-900">
                      {editPasswordUser.companyName || editPasswordUser.invitation?.companyName || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-slate-500">{"Role"}</span>
                    <span className="px-2.5 py-1 bg-primary/10 rounded-full text-primary text-xs font-bold capitalize">
                      {editPasswordUser.role.replace("_", " ")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="h-px bg-slate-200" />

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="new-password" className="text-slate-700">{"New Password"}</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder={"Min. 8 characters"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-slate-50 rounded-2xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-slate-700">{"Confirm Password"}</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder={"Re-enter new password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-slate-50 rounded-2xl"
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500">{"Passwords do not match."}</p>
                  )}
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-2xl"
              onClick={() => {
                setEditPasswordUser(null);
                setNewPassword("");
                setConfirmPassword("");
              }}
            >
              {"Cancel"}
            </Button>
            <Button
              className="rounded-2xl"
              onClick={() =>
                editPasswordUser &&
                updatePasswordMutation.mutate({ userId: editPasswordUser.id, newPassword })
              }
              disabled={
                !newPassword ||
                newPassword.length < 8 ||
                newPassword !== confirmPassword ||
                updatePasswordMutation.isPending
              }
            >
              {updatePasswordMutation.isPending ? "Updating..." : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
